import React, { useState, useEffect, useReducer, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Fab from "@material-ui/core/Fab";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import FolderIcon from "@material-ui/icons/Folder";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FileModal from "../../components/FileModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
    if (action.type === "LOAD_FILES") {
        const files = action.payload;
        const newFiles = [];
        files.forEach((f) => {
            const idx = state.findIndex((s) => s.id === f.id);
            if (idx !== -1) state[idx] = f; else newFiles.push(f);
        });
        return [...state, ...newFiles];
    }
    if (action.type === "UPDATE_FILES") {
        const f = action.payload;
        const idx = state.findIndex((s) => s.id === f.id);
        if (idx !== -1) { state[idx] = f; return [...state]; }
        return [f, ...state];
    }
    if (action.type === "DELETE_FILE") {
        const idx = state.findIndex((s) => s.id === action.payload);
        if (idx !== -1) state.splice(idx, 1);
        return [...state];
    }
    if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1, padding: theme.spacing(1),
        overflowY: "scroll", ...theme.scrollbarStyles,
    },
    mobileList: {
        display: "flex", flexDirection: "column",
        gap: theme.spacing(1.5), padding: theme.spacing(1),
    },
    card: {
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10, padding: theme.spacing(1.5),
        display: "flex", alignItems: "center", gap: theme.spacing(1.5),
    },
    cardIcon: {
        width: 42, height: 42, borderRadius: 10,
        background: "rgba(91,45,142,0.1)", color: "#5B2D8E",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    },
    cardName: {
        flex: 1, fontWeight: 600, fontSize: "0.95rem",
        color: theme.palette.text.primary,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    },
    cardActions: { display: "flex", flexShrink: 0, gap: 2 },
    mobileHeader: {
        display: "flex", flexDirection: "column",
        gap: theme.spacing(1), width: "100%", padding: theme.spacing(0, 0, 1),
    },
    mobileHeaderRow: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    fab: { position: "fixed", bottom: theme.spacing(3), right: theme.spacing(3), zIndex: 10 },
    emptyState: {
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: theme.spacing(6, 2),
        color: theme.palette.text.secondary, gap: theme.spacing(1),
    },
}));

const FileLists = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { user, socket } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedFileList, setSelectedFileList] = useState(null);
    const [deletingFileList, setDeletingFileList] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [files, dispatch] = useReducer(reducer, []);
    const [fileListModalOpen, setFileListModalOpen] = useState(false);

    const fetchFileLists = useCallback(async () => {
        try {
            const { data } = await api.get("/files/", { params: { searchParam, pageNumber } });
            dispatch({ type: "LOAD_FILES", payload: data.files });
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) { toastError(err); }
    }, [searchParam, pageNumber]);

    useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const t = setTimeout(() => { fetchFileLists(); }, 500);
        return () => clearTimeout(t);
    }, [searchParam, pageNumber, fetchFileLists]);

    useEffect(() => {
        const onEvent = (data) => {
            if (data.action === "update" || data.action === "create") dispatch({ type: "UPDATE_FILES", payload: data.files });
            if (data.action === "delete") dispatch({ type: "DELETE_FILE", payload: +data.fileId });
        };
        socket.on(`company-${user.companyId}-file`, onEvent);
        return () => socket.off(`company-${user.companyId}-file`, onEvent);
    }, [socket]);

    const handleOpenModal = () => { setSelectedFileList(null); setFileListModalOpen(true); };
    const handleCloseModal = () => { setSelectedFileList(null); setFileListModalOpen(false); };
    const handleSearch = (e) => setSearchParam(e.target.value);
    const handleEdit = (f) => { setSelectedFileList(f); setFileListModalOpen(true); };
    const handleDelete = async (id) => {
        try {
            await api.delete(`/files/${id}`);
            toast.success(i18n.t("files.toasts.deleted"));
        } catch (err) { toastError(err); }
        setDeletingFileList(null);
        setSearchParam("");
        dispatch({ type: "RESET" });
        setPageNumber(1);
        await fetchFileLists();
    };
    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title={deletingFileList && i18n.t("files.confirmationModal.deleteTitle")}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDelete(deletingFileList.id)}
            >
                {i18n.t("files.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <FileModal
                open={fileListModalOpen}
                onClose={handleCloseModal}
                reload={fetchFileLists}
                aria-labelledby="form-dialog-title"
                fileListId={selectedFileList && selectedFileList.id}
            />

            {user.profile === "user" ? <ForbiddenPage /> : (
                <>
                    {isMobile ? (
                        <>
                            <MainHeader>
                                <div className={classes.mobileHeader}>
                                    <div className={classes.mobileHeaderRow}>
                                        <Title>{i18n.t("files.title")}</Title>
                                        <Typography variant="caption" color="textSecondary">
                                            {files.length} archivos
                                        </Typography>
                                    </div>
                                    <TextField
                                        fullWidth size="small" variant="outlined"
                                        placeholder={i18n.t("contacts.searchPlaceholder")}
                                        type="search" value={searchParam} onChange={handleSearch}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon style={{ color: "gray" }} fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </div>
                            </MainHeader>
                            <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                                {files.length === 0 && !loading ? (
                                    <div className={classes.emptyState}>
                                        <FolderIcon style={{ fontSize: 48, opacity: 0.3 }} />
                                        <Typography variant="body2">Sin archivos</Typography>
                                    </div>
                                ) : (
                                    <div className={classes.mobileList}>
                                        {files.map((f) => (
                                            <div key={f.id} className={classes.card}>
                                                <div className={classes.cardIcon}>
                                                    <FolderIcon fontSize="small" />
                                                </div>
                                                <div className={classes.cardName}>{f.name}</div>
                                                <div className={classes.cardActions}>
                                                    <IconButton size="small" onClick={() => handleEdit(f)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingFileList(f); }}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        ))}
                                        {loading && (
                                            <Typography variant="caption" align="center" color="textSecondary">
                                                Cargando...
                                            </Typography>
                                        )}
                                    </div>
                                )}
                            </Paper>
                            <Fab color="primary" className={classes.fab} onClick={handleOpenModal}>
                                <AddIcon />
                            </Fab>
                        </>
                    ) : (
                        <>
                            <MainHeader>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 16 }}>
                                    <Title>{i18n.t("files.title")} ({files.length})</Title>
                                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                        <TextField
                                            placeholder={i18n.t("contacts.searchPlaceholder")}
                                            type="search" value={searchParam} onChange={handleSearch}
                                            size="small" variant="outlined"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon style={{ color: "gray" }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Button variant="contained" color="primary" onClick={handleOpenModal}>
                                            {i18n.t("files.buttons.add")}
                                        </Button>
                                    </div>
                                </div>
                            </MainHeader>
                            <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">{i18n.t("files.table.name")}</TableCell>
                                            <TableCell align="center">{i18n.t("files.table.actions")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {files.map((f) => (
                                            <TableRow key={f.id}>
                                                <TableCell align="center">{f.name}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" onClick={() => handleEdit(f)}><EditIcon /></IconButton>
                                                    <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingFileList(f); }}>
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {loading && <TableRowSkeleton columns={2} />}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </>
                    )}
                </>
            )}
        </MainContainer>
    );
};

export default FileLists;
